import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import utilityApi from '~/apis/utility';
import { CloseIcon, InformationFillIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch } from '~/redux/hooks';
import { handleGetSensorTypes } from '~/thunks/master/masterThunk';
import { handleApiError, notify } from '~/utils/helper';
import { IEquipment } from '~/utils/interface/equipment';
import orderApi from '~/apis/order';
import { IOrder, IPlan } from '~/utils/interface/order';
import notificationApi from '~/apis/notification';
import { orderBy } from 'lodash';
import { BaseScrollbar } from '~/components/common';
import OrderHistoryDialog from '../order-history/OrderHistoryDialog';

type ServiceDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
  data?: IEquipment;
};

type StatusColor = { [key: string]: string };
const statusColors: StatusColor = {
  0: '#72777B',
  1: '#3D9149',
  2: '#FF9500',
  3: '#F32A4C',
};

const orderStatusColor: StatusColor = {
  0: '#F32A4C',
  1: '#F32A4C',
  2: '#3D9149',
  3: '#F32A4C',
  4: '#F32A4C',
};

const ServiceDialog = (props: ServiceDialogProps) => {
  const { t } = useTranslation('admin/equipment');
  const dispatch = useAppDispatch();
  const [agents, setAgents] = useState<any[]>([]);
  const [orderHistories, setOrderHistories] = useState<IOrder[]>([]);
  const [pendingOrder, setPendingOrder] = useState<IOrder>();
  const [plans, setPlans] = useState<IPlan[]>([]);
  const [isOpenOrderHistoryDialog, setIsOpenOrderHistoryDialog] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<IOrder | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      (async () => {
        try {
          const response = await utilityApi.getListAgents();
          setAgents(response.data);
        } catch (error) {
          handleApiError(error);
          setAgents([]);
        }
      })();

      dispatch(handleGetSensorTypes());

      getOrder();

      (async () => {
        try {
          const response = await orderApi.getPlans();
          if (response.data) setPlans(response.data);
        } catch (error) {
          handleApiError(error);
          setPlans([]);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const getOrder = async () => {
    try {
      const response = await orderApi.getByDevice({
        device_id: Number(props?.data?.device_id),
        order_by: {
          field: 'created_at',
          type: 'desc',
        },
      });
      if (response.data) {
        setOrderHistories(orderBy(response.data, ['created_at'], ['desc']));
        setPendingOrder(response.data.find((item) => item.order_status === 1));
      }
    } catch (error) {
      handleApiError(error);
      setOrderHistories([]);
      setPendingOrder(undefined);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getStatus = (status: number | undefined) => {
    const color = statusColors[String(status) as keyof StatusColor];
    return (
      <Box className={`text-[${color}] border border-[${color}] rounded-[20px] px-[10px] py-[5.5px]`}>
        {t(`simStatuses.${status}`)}
      </Box>
    );
  };

  const getAgentName = (agent: string | undefined) => {
    const data = agents.find((item) => item.username === agent);
    if (data) return `${data.username} - ${data.name}`;
  };

  const sendNotification = async () => {
    try {
      await notificationApi.send({ device_code: String(props?.data?.device_code) });
      notify('success', t('serviceDialog.sendNotificationSuccess'));
    } catch (e: any) {
      handleApiError(e);
    }
  };

  const confirmOrder = async () => {
    try {
      await orderApi.confirmOrder({
        order_id: Number(pendingOrder?.order_id),
        paid_date: pendingOrder?.paid_date || dayjs().format('YYYY-MM-DD HH:mm:ss'),
      });

      notify('success', t('serviceDialog.confirmSuccess'));
      getOrder();
    } catch (e: any) {
      handleApiError(e);
    }
  };

  const cancelOrder = async () => {
    try {
      await orderApi.cancelOrder({
        order_id: Number(pendingOrder?.order_id),
      });

      notify('success', t('serviceDialog.cancelSuccess'));
      getOrder();
    } catch (e: any) {
      handleApiError(e);
    }
  };

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='lg'>
        <DialogTitle className='bg-[#005F73]'>
          {t('serviceDialog.title')}
          <IconButton onClick={() => handleSetOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <BaseRow className='gap-[100px]'>
            <Box className='w-1/2 relative'>
              <Button
                className='absolute top-[10%] right-0'
                variant='contained'
                color='secondary'
                onClick={sendNotification}
              >
                {t('serviceDialog.sendNotification')}
              </Button>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('serviceDialog.deviceCode')}:</Box>
                <Box>{props.data?.device_code}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('serviceDialog.deviceName')}:</Box>
                <Box>{props.data?.device_name}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('serviceDialog.simNo')}:</Box>
                <Box>{props.data?.sim_no}</Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('serviceDialog.simStatus')}:</Box>
                {getStatus(props.data?.sim_status)}
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('serviceDialog.simExpireDate')}:</Box>
                <Box className={`text-[${statusColors[props.data?.sim_status as keyof StatusColor]}]`}>
                  {props.data?.sim_expire_date ? dayjs(props.data?.sim_expire_date).format('DD/M/YYYY') : null}
                </Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('serviceDialog.iotStatus')}:</Box>
                {getStatus(props.data?.iot_status)}
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px] items-center'>
                <Box className='font-bold'>{t('serviceDialog.expireDate')}:</Box>
                <Box className={`text-[${statusColors[props.data?.iot_status as keyof StatusColor]}]`}>
                  {props.data?.expire_date ? dayjs(props.data?.expire_date).format('DD/M/YYYY') : null}
                </Box>
              </Box>
              <Box className='flex flex-row gap-[15px] mb-[10px]'>
                <Box className='font-bold'>{t('serviceDialog.agent')}:</Box>
                <Box>{getAgentName(props.data?.agent)}</Box>
              </Box>
              <Box className='mb-[26px]'>
                <BaseRow className='mb-[5px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('serviceDialog.orderHistory')}</Box>
                  <Box className='font-bold rounded-full text-[#FFFFFF] bg-[#005F73] w-[30px] h-[30px] flex justify-center items-center'>
                    {orderHistories.length || 0}
                  </Box>
                </BaseRow>
                <BaseScrollbar maxHeight='400px'>
                  {orderHistories.length ? (
                    <Box className='rounded-[10px] overflow-hidden border-[1px] border-[#005F73]'>
                      <Table>
                        <TableBody className='divide-y divide-[#005F73]'>
                          {orderHistories.map((row, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell className='border-none'>
                                  <Box>
                                    <Box className='font-bold mb-[5px]'>
                                      {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY HH:mm') : ''}
                                    </Box>
                                    <Box>
                                      {plans.find((plan) => plan.plan_id === row.items?.[0].plan_id)?.description}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className='border-none text-right'>
                                  <Box>
                                    <Box className='mb-[5px]'></Box>
                                    <Box
                                      className={`border rounded-[2em] font-bold border-[${
                                        orderStatusColor[row.order_status]
                                      }] text-[${orderStatusColor[row.order_status]}] w-[120px] text-center ml-auto`}
                                    >
                                      {t(`orderStatuses.${row.order_status}`)}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell className='border-none w-[40px]'>
                                  <BaseRow>
                                    <IconButton
                                      onClick={() => {
                                        setSelectedOrder(row);
                                        setIsOpenOrderHistoryDialog(true);
                                      }}
                                    >
                                      <InformationFillIcon className='[&>path]:fill-[#D9D9D9]' />
                                    </IconButton>
                                  </BaseRow>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  ) : null}
                </BaseScrollbar>
              </Box>
            </Box>
            <Box className='w-1/2'>
              <Box className='mb-[36px]'>
                <BaseRow className='mb-[13px] gap-[15px] w-full !justify-between !items-center'>
                  <Box className='font-bold text-[#005F73] text-[18px]'>{t('serviceDialog.requestRenew')}</Box>
                </BaseRow>
                {pendingOrder ? (
                  <Box className='flex flex-col gap-[8px] rounded-[10px] overflow-hidden border-[1px] border-[#005F73] px-[21px] pt-[10px] pb-[35px]'>
                    <Box className='font-bold'>
                      {pendingOrder.created_at ? dayjs(pendingOrder.created_at).format('DD/MM/YYYY HH:mm') : ''}
                    </Box>
                    <Box>{plans.find((plan) => plan.plan_id === pendingOrder.items?.[0].plan_id)?.description}</Box>
                    <Box className='border-b border-[#DFDFDF] w-full'></Box>
                    <BaseRow className='!justify-between'>
                      <Box className='font-bold'>{t('serviceDialog.fullname')}:</Box>
                      <Box>{pendingOrder?.einvoice_info?.customer_name || pendingOrder?.contact_info?.fullname}</Box>
                    </BaseRow>
                    <BaseRow className='!justify-between'>
                      <Box className='font-bold'>{t('serviceDialog.phone')}:</Box>
                      <Box>{pendingOrder?.einvoice_info?.phone || pendingOrder?.contact_info?.phone}</Box>
                    </BaseRow>
                    <BaseRow className='!justify-between'>
                      <Box className='font-bold'>{t('serviceDialog.email')}:</Box>
                      <Box>{pendingOrder?.einvoice_info?.email || pendingOrder?.contact_info?.email}</Box>
                    </BaseRow>
                    <BaseRow className='!justify-between'>
                      <Box className='font-bold'>{t('serviceDialog.address')}:</Box>
                      <Box>{pendingOrder?.einvoice_info?.customer_address || pendingOrder?.contact_info?.address}</Box>
                    </BaseRow>
                    <BaseRow className='!justify-between'>
                      <Box className='font-bold'>{t('serviceDialog.note')}:</Box>
                      <Box>{pendingOrder?.notes}</Box>
                    </BaseRow>
                    <BaseRow className='gap-[13px] !justify-center'>
                      <Button
                        variant='outlined'
                        color='success'
                        className='h-[23px] rounded-[5px] min-w-[117px]'
                        onClick={() => confirmOrder()}
                      >
                        {t('serviceDialog.confirm')}
                      </Button>
                      <Button
                        variant='outlined'
                        color='secondary'
                        className='h-[23px] rounded-[5px] min-w-[117px]'
                        onClick={() => cancelOrder()}
                      >
                        {t('serviceDialog.cancel')}
                      </Button>
                    </BaseRow>
                  </Box>
                ) : (
                  <Box className='rounded-[10px] border-[1px] border-[#005F73] p-[20px] text-center'>
                    {t('serviceDialog.noPendingOrder')}
                  </Box>
                )}
              </Box>
            </Box>
          </BaseRow>
        </DialogContent>
        <OrderHistoryDialog
          open={isOpenOrderHistoryDialog}
          setOpen={(isOpen) => setIsOpenOrderHistoryDialog(isOpen)}
          data={selectedOrder}
          plans={plans}
          equipmentData={props.data}
          agents={agents}
        />
      </Dialog>
    </>
  );
};

export default memo(ServiceDialog);
